import React, { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Stack } from '@mui/material'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Badge,
  DeleteOutlineOutlined as DeleteIcon,
  EditOutlined as EditIcon,
  Settings,
  Download,
  Print
} from '@material-ui/icons'
import * as XLSX from 'xlsx'
import { toast } from 'react-toastify'
import { badgesSubjectsApplication, Collection, possibleSheetNamesForBadges } from 'constants/index'
import ClearIcon from '@mui/icons-material/Clear'
import ProgressButton from 'components/ProgressButton'
import badgesSlice, {
  applicableBadgeConfiguration,
  bulkCreateBadgeSubjectsData,
  deleteBadgeSubject,
  deleteMultipleBadgeSubjects,
  getBadgeSubjectsData
} from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import {
  formatToFourDigits,
  getFormattedFullName,
  getFormattedLocation,
  loadImageBase64
} from 'utils'
import { renderCol } from 'utils/renderColumns'
import getTableOptions from 'utils/getTableOptions'
import Tooltip from '@material-ui/core/Tooltip'
import BasicTable from 'components/BasicTable'
import ResourceNotFound from 'components/ResourceNotFound'
import ConfirmModal from 'components/ConfirmationModal'
import BadgeSubjectForm from './BadgeSubjectForm'
import { jsPDF } from 'jspdf'
import QRCode from 'qrcode'
import { first } from 'lodash'
import { storageRef } from '../../../index'
import { Form, Formik } from 'formik'
import CustomSelect from 'components/Form/CustomSelect'
import { pdfConfigurationSchema } from 'schemas/badges'
import { adjustFontSize, adjustFontSizeForLocation, getBadgeDimensions } from 'utils/badges'
import { getContrastColor } from 'utils/colors'
import { downloadBadgeSubjectsInCSV } from '../../../utils/downloadCSV'

const BadgePrinting = () => {
  const inputFileRef = useRef()
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()

  const { conference } = useSelector((state) => state.conference)
  const [openImportBadgeDataModal, setOpenImportBadgeDataModal] = useState(false)
  const [parsingFile, setParsingFile] = useState(false)
  const [addingData, setAddingData] = useState(false)
  const [excelFile, setExcelFile] = useState(null)
  const [parsedFileData, setParsedFileData] = useState(null)
  const [deletedSubjectId, setDeletedSubjectId] = useState('')
  const [deletingSubject, setDeletingSubject] = useState('')

  const [editedSubjectId, setEditedSubjectId] = useState(false)
  const [openAddSubjectModal, setOpenAddSubjectModal] = useState(false)
  const [printingBadges, setPrintingBadges] = useState(false)
  const [deletingSubjects, setDeletingSubjects] = useState(false)
  const [filter, setFilter] = useState(badgesSubjectsApplication[0])
  const [filteredSubjectsData, setFilteredSubjectsData] = useState([])

  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false)
  const [deletedSubjects, setDeletedSubjects] = useState([])

  const [subjectsToBePrinted, setSubjectsToBePrinted] = useState([])
  const [pdfConfigModal, setPdfConfigModal] = useState(false)
  const [submissionType, setSubmissionType] = useState(null)
  const [isPrinting, setIsPrinting] = useState(false)

  const openPdfConfigModal = (subjects) => {
    setSubjectsToBePrinted(subjects)
    setPdfConfigModal(true)
  }
  const closePdfConfigModal = () => {
    setSubjectsToBePrinted([])
    setPdfConfigModal(false)
  }

  const deleteSubjectsInBulk = async () => {
    setDeletingSubjects(true)
    try {
      await deleteMultipleBadgeSubjects(deletedSubjects)
    } catch (err) {
      toast.error('Failed to delete subject(s). Please try again.')
    } finally {
      setDeletingSubjects(false)
    }
  }

  const handleCloseBulkDeleteModal = () => {
    setOpenBulkDeleteModal(false)
  }

  const { badgesSubjectsData, selectedSubjectsRows, subjectsCurrentPage, subjectsPageSize } =
    useSelector((state) => state.badges)

  const handleRowSelectionChange = (currentRowsSelected, allRowsSelected) => {
    const selected = allRowsSelected?.map((row) => row?.dataIndex)
    dispatch(badgesSlice.actions.selectedSubjectsRows(selected))
  }

  useEffect(() => {
    if (!badgesSubjectsData?.length) {
      setFilteredSubjectsData([])
    } else if (badgesSubjectsData?.length && filter) {
      setFilteredSubjectsData(badgesSubjectsData?.filter((subject) => subject.Type === filter))
    }
    return () => dispatch(badgesSlice.actions.selectedSubjectsRows([]))
  }, [badgesSubjectsData, filter])

  const handleOpenAddSubjectModal = () => setOpenAddSubjectModal(true)
  const handleCloseAddSubjectModal = () => {
    setOpenAddSubjectModal(false)
    setEditedSubjectId('')
  }

  const handleCloseDeleteModal = () => {
    setDeletedSubjectId('')
  }

  const handleDeleteBadgeSubject = async () => {
    setDeletingSubject(true)
    await deleteBadgeSubject(deletedSubjectId)
    setDeletingSubject(false)
    dispatch(badgesSlice.actions.selectedSubjectsRows([]))
  }

  useEffect(() => {
    if (conference?.id) {
      const unsub = getBadgeSubjectsData(conference?.id, (data) => {
        dispatch(badgesSlice.actions.setBadgesSubjectsData(data))
      })
      return () => unsub && unsub()
    }

    return () => {
      dispatch(badgesSlice.actions.selectedSubjectsRows([]))
    }
  }, [conference?.id])

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) {
      toast.warning('No file uploaded')
      return
    }
    setParsingFile(true)
    setExcelFile(file)
    const reader = new FileReader()
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result)
      const workbook = XLSX.read(data, { type: 'array' })
      const sheetNames = workbook.SheetNames
      const parsedData = {}

      const validSheetNames = sheetNames?.some((sheetName) =>
        possibleSheetNamesForBadges.includes(sheetName.toLowerCase())
      )

      if (!validSheetNames || !sheetNames?.length) {
        toast.warning(
          'The uploaded Excel file must include the following valid sheet names: Attendees, Exhibitors, Speakers, and Sponsors.'
        )
        setParsingFile(false)
        event.target.value = null
        handleCloseBadgesDataModal()
        return
      }

      sheetNames.forEach((sheetName) => {
        if (sheetName && possibleSheetNamesForBadges.includes(sheetName?.toLowerCase())) {
          parsedData[sheetName?.toLowerCase()] = XLSX.utils.sheet_to_json(
            workbook.Sheets[sheetName]
          )
        }
      })

      setParsedFileData(parsedData)
      setParsingFile(false)
    }
    reader.readAsArrayBuffer(file)
    event.target.value = null
  }

  const handleOpenBadgesDataModal = () => setOpenImportBadgeDataModal(true)
  const handleCloseBadgesDataModal = () => {
    setExcelFile(null)
    setParsedFileData(null)
    setOpenImportBadgeDataModal(false)
  }
  const navigateToBadgeConfiguration = () => history.push(`${location.pathname}/configuration`)

  const handleImportSubjects = async () => {
    setAddingData(true)
    await bulkCreateBadgeSubjectsData(parsedFileData, conference?.id)
    setAddingData(false)
    handleCloseBadgesDataModal()
  }

  const handleFormSubmit = async (values) => {
    localStorage.setItem('badgeOrientation', values.orientation)
    if (submissionType === 'download') {
      await downloadPdfFile(values)
    } else if (submissionType === 'print') {
      await printPdfFile(values)
    }
  }

  const downloadPdfFile = async (values) => {
    setPrintingBadges(true)
    try {
      await downloadBadgesInPdfFile(
        subjectsToBePrinted,
        first(subjectsToBePrinted).Type,
        values.pageSize,
        values.orientation,
        'download'
      )
    } catch (err) {
      toast.error('Failed to generate PDF file. Please try again.')
    } finally {
      setPrintingBadges(false)
      closePdfConfigModal()
    }
  }

  const printPdfFile = async (values) => {
    setIsPrinting(true)
    try {
      await downloadBadgesInPdfFile(
        subjectsToBePrinted,
        first(subjectsToBePrinted).Type,
        values.pageSize,
        values.orientation,
        'print'
      )
    } catch (err) {
      toast.error('Something went wrong while generating the PDF to print. Please try again.')
    } finally {
      setIsPrinting(false)
      closePdfConfigModal()
    }
  }

  const downloadBadgesInPdfFile = async (subjects, type, pageSize, orientation, submissionType) => {
    try {
      const {
        isLandscape,
        startX,
        startY,
        logoWidth,
        logoHeight,
        qrHeight,
        qrWidth,
        badgeWidth,
        badgeHeight,
        spacingX,
        spacingY,
        badgePerRow,
        badgePerColumn
      } = getBadgeDimensions(orientation, pageSize)

      const applicableConfig = await applicableBadgeConfiguration(type, conference?.id)
      if (!applicableConfig) {
        toast.warning(`No print configuration found for ${type.toLowerCase()} subject.`)
        return
      }

      const { id, confLogo } = conference

      let logoUrl = null
      try {
        logoUrl = await storageRef
          .child(`${id}/${Collection.CONFERENCES}/${confLogo}`)
          .getDownloadURL()
      } catch (err) {
        toast.warning(
          `No conference logo found. Please upload a conference logo before printing badges.`
        )
        return
      }
      const logoBase64 = await loadImageBase64(logoUrl)

      const fieldsToEncodeInQRCode = applicableConfig?.fieldsAppliedInsideQRCode
      const qrCodePromises = subjects?.map((subject) => {
        const dataToEncodeInQRCode = {}
        fieldsToEncodeInQRCode.forEach((field) => {
          dataToEncodeInQRCode[field] = subject[field]
        })

        const order = ['fullName', 'position', 'organization', 'location', 'Email', 'Phone']
        const fullName = getFormattedFullName(
          dataToEncodeInQRCode?.FullName,
          dataToEncodeInQRCode?.Credential
        )
        const position = dataToEncodeInQRCode?.['Position_Title'] ?? null
        const organization = dataToEncodeInQRCode?.Organization
        const location = getFormattedLocation(
          dataToEncodeInQRCode?.City,
          dataToEncodeInQRCode?.State,
          dataToEncodeInQRCode?.Country
        )

        const modifiedResultToBeEncoded = {
          fullName,
          position,
          organization,
          location,
          Email: dataToEncodeInQRCode?.Email,
          Phone: dataToEncodeInQRCode?.Phone
        }

        const resultToBeEncoded = order
          .map((key) => modifiedResultToBeEncoded[key])
          .filter((value) => value !== undefined && value !== null && value !== '')
          .join('\n')

        return QRCode.toDataURL(resultToBeEncoded ? resultToBeEncoded : ' ', {
          margin: 0
        })
      })
      const qrCodes = await Promise.all(qrCodePromises)

      // Initialize PDF Document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'in',
        format: pageSize,
        compress: true
      })

      const fieldsToAppearOnBadge = applicableConfig?.fieldsAppliedInsideBadge?.length
        ? [...applicableConfig.fieldsAppliedInsideBadge, 'counter']
        : ['counter']

      // Helper function to draw a badge
      const drawBadge = (data, qrCode, x, y) => {
        const dataToAppearOnBadge = {}
        fieldsToAppearOnBadge.forEach((field) => {
          dataToAppearOnBadge[field] = data[field]
          dataToAppearOnBadge['Type'] = type
        })

        // Draw border
        // doc.setLineWidth(0.01).rect(x, y, badgeWidth, badgeHeight)

        // Badge logo
        doc.addImage(logoBase64, 'PNG', x + 0.1, y + 0.1, logoWidth, logoHeight)

        // Add text
        const textX = x + badgeWidth / 2
        const textOptions = { align: 'center' }
        let textY = y + logoHeight

        // Full Name
        textY = isLandscape ? textY + 0.31 : textY + 0.34
        const fullName = getFormattedFullName(
          dataToAppearOnBadge?.FullName,
          dataToAppearOnBadge?.Credential
        )
        doc.setTextColor('#000000')
        doc.setFont('helvetica', 'bold')
        if (fullName.trim()) {
          const { fontSize } = adjustFontSize(fullName, 13.5)
          doc.setFontSize(fontSize).text(fullName, textX, textY, textOptions)
          textY += 0.22
        }

        // Position and Title
        const positionAndTitle = dataToAppearOnBadge?.['Position_Title'] ?? null
        if (positionAndTitle) {
          const { fontSize } = adjustFontSizeForLocation(positionAndTitle, 12)
          doc.setFontSize(fontSize).setFont('helvetica', 'normal')
          doc.text(positionAndTitle, textX, textY, textOptions)
          textY += 0.22
        }

        // Organization
        if (dataToAppearOnBadge?.Organization) {
          const { fontSize } = adjustFontSizeForLocation(dataToAppearOnBadge?.Organization, 12)
          doc.setFontSize(fontSize).setFont('helvetica', 'normal')
          doc.text(dataToAppearOnBadge?.Organization, textX, textY, textOptions)
          textY += 0.22
        }

        const location = getFormattedLocation(dataToAppearOnBadge?.City, dataToAppearOnBadge?.State)
        if (location) {
          const { fontSize } = adjustFontSizeForLocation(location, 12)
          doc.setFontSize(fontSize).setFont('helvetica', 'normal')
          doc.text(location, textX, textY, textOptions)
          textY += 0.22
        }

        if (dataToAppearOnBadge?.Country) {
          const { fontSize } = adjustFontSizeForLocation(dataToAppearOnBadge?.Country, 12)
          doc.setFontSize(fontSize).setFont('helvetica', 'normal')
          doc.text(dataToAppearOnBadge?.Country, textX, textY, textOptions)
        }

        // Add QR code
        const qrCodeX = isLandscape ? x + 0.12 : x + badgeWidth / 2 - 0.5
        const qrCodeY = isLandscape ? y + logoHeight + 0.45 : textY + 0.15
        doc.addImage(qrCode, 'PNG', qrCodeX, qrCodeY, qrWidth, qrHeight)

        // Badge Counter
        if (dataToAppearOnBadge?.counter && formatToFourDigits(dataToAppearOnBadge?.counter)) {
          doc.setFontSize(10.5).setFont('helvetica', 'bold')
          doc.text(
            formatToFourDigits(dataToAppearOnBadge?.counter),
            isLandscape ? x + 0.1 + qrWidth / 2 : x + badgeWidth / 2,
            isLandscape ? y + logoHeight + qrHeight + 0.58 : textY + qrHeight + 0.3,
            textOptions
          )
        }

        // Subject role
        const rectYPosition = y + logoHeight + qrHeight + (isLandscape ? 0.625 : 1.55)
        const fillColor = applicableConfig?.badgeBarColor ?? '#000000'
        const textColor = getContrastColor(
          applicableConfig?.badgeBarColor ? applicableConfig?.badgeBarColor : '#000000'
        )
        doc.setFillColor(fillColor).rect(x + 0.1, rectYPosition, badgeWidth - 0.2, 0.35, 'F')
        doc
          .setTextColor(textColor)
          .setFontSize(12)
          .setFont('helvetica', 'bold')
          .text(
            `${dataToAppearOnBadge?.Type === 'Speaker' ? 'Faculty' : dataToAppearOnBadge?.Type}`,
            x + badgeWidth / 2,
            rectYPosition + 0.23,
            {
              align: 'center'
            }
          )
      }

      // Layout badges
      let x = startX,
        y = startY

      subjects.forEach((subject, index) => {
        const qrCode = qrCodes[index]
        drawBadge(subject, qrCode, x, y)

        // Update position
        x += badgeWidth + spacingX
        if ((index + 1) % badgePerRow === 0) {
          x = startX
          y += badgeHeight + spacingY
        }

        // Add new page if grid is full
        if ((index + 1) % (badgePerRow * badgePerColumn) === 0 && index + 1 !== subjects.length) {
          doc.addPage()
          x = startX
          y = startY
        }
      })

      // Save the file
      if (submissionType === 'download') {
        doc.save(`${type}s_badges_${orientation}.pdf`)
      } else if (submissionType === 'print') {
        const base64String = doc.output('datauristring').split(',')[1]
        printBase64Pdf(base64String)
      }
    } catch (err) {
      console.error(err.message)
      toast.error('Something went wrong while printing the badge(s). Please try again.')
    } finally {
      dispatch(badgesSlice.actions.selectedSubjectsRows([]))
    }
  }

  function printBase64Pdf(base64String) {
    try {
      // Convert Base64 string to a Uint8Array buffer
      const binaryData = Uint8Array.from(atob(base64String), (c) => c.charCodeAt(0))
      const blob = new Blob([binaryData], { type: 'application/pdf' })

      // Create Object URL
      const blobUrl = URL.createObjectURL(blob)

      // Create hidden iframe
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = blobUrl
      document.body.appendChild(iframe)

      // Wait for iframe to load, then trigger print
      iframe.onload = function () {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
      }
    } catch (error) {
      console.error('Error printing PDF:', error)
    }
  }

  const tableHead = [
    'Badge #',
    'Full Name',
    'Email',
    'Position/Title',
    'Organization',
    'Type',
    {
      name: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => renderCol(value),
        customHeadLabelRender: (meta) => renderCol(meta.label)
      }
    }
  ]
  const options = getTableOptions()

  const formatTableData = (data) =>
    data.map((obj) => {
      return [
        obj?.counter || 'N/A',
        obj?.FullName || 'N/A',
        obj?.Email || 'N/A',
        obj?.['Position_Title'] || 'N/A',
        obj?.Organization || 'N/A',
        obj?.Type || 'N/A',
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Print Badge">
            <IconButton
              className={classes.iconButton}
              onClick={() => openPdfConfigModal([obj])}
              size="small">
              <Badge color="action" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton className={classes.iconButton} onClick={() => setEditedSubjectId(obj?.id)}>
              <EditIcon color="action" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton className={classes.iconButton} onClick={() => setDeletedSubjectId(obj?.id)}>
              <DeleteIcon color="action" />
            </IconButton>
          </Tooltip>
        </Box>
      ]
    })

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h4">Badge Printing</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            variant="contained"
            color="primary"
            className={classes.configButton}
            onClick={handleOpenAddSubjectModal}>
            Add Subject
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.configButton}
            onClick={handleOpenBadgesDataModal}>
            Import Subjects
          </Button>
          <Button
            variant="contained"
            startIcon={<Settings />}
            color="primary"
            className={classes.configButton}
            onClick={navigateToBadgeConfiguration}>
            Badge Print Configurations
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterDropdown}>
        <Typography className={classes.filterLabel}>Filter by: </Typography>
        <FormControl fullWidth variant="outlined" size="small" className={classes.select}>
          <Select
            value={filter}
            onChange={(event) => {
              setFilter(event.target.value)
              dispatch(badgesSlice.actions.selectedSubjectsRows([]))
            }}>
            {badgesSubjectsApplication.map((e) => (
              <MenuItem value={e} key={e}>
                {e}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {filteredSubjectsData?.length ? (
        <Paper variant="outlined" style={{ marginTop: 20 }}>
          <BasicTable
            data={formatTableData(filteredSubjectsData)}
            columns={tableHead}
            options={{
              ...options,
              fixedHeader: true,
              selectableRowsHeader: true,
              selectableRows: 'multiple',
              selectableRowsHideCheckboxes: false,
              onRowSelectionChange: handleRowSelectionChange,
              rowsSelected: selectedSubjectsRows.map((dataIndex) =>
                filteredSubjectsData.findIndex((item, idx) => idx === dataIndex)
              ),
              customToolbarSelect: (selectedRows) => (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '8px 12px' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      const subjectBadgesToPrinted = selectedRows.data.map(
                        (d) => filteredSubjectsData[d.dataIndex]
                      )
                      openPdfConfigModal(subjectBadgesToPrinted)
                    }}>
                    Print Badges
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={classes.exportButton}
                    onClick={async () => {
                      const selectedSubjects = selectedRows.data.map(
                        (d) => filteredSubjectsData[d.dataIndex]
                      )
                      await downloadBadgeSubjectsInCSV(selectedSubjects[0]?.Type, selectedSubjects)
                    }}>
                    Export Badge Data
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.deleteBtn}
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      setOpenBulkDeleteModal(true)
                      const subjectsToBeDeleted = selectedRows.data.map(
                        (d) => filteredSubjectsData[d.dataIndex]
                      )
                      setDeletedSubjects(subjectsToBeDeleted)
                    }}
                    disabled={printingBadges}>
                    Delete Selected
                  </Button>
                </Box>
              ),
              onChangePage: (page) => dispatch(badgesSlice.actions.setSubjectsCurrentPage(page)),
              onChangeRowsPerPage: (size) =>
                dispatch(badgesSlice.actions.setSubjectsPageSize(size)),
              page: subjectsCurrentPage,
              rowsPerPage: subjectsPageSize
            }}
          />
        </Paper>
      ) : (
        <ResourceNotFound message={`No ${filter} found for badge printing`} />
      )}

      <Dialog fullWidth maxWidth="md" open={openImportBadgeDataModal}>
        <DialogContent className={classes.modalContent}>
          <Stack direction="row" className={classes.modalHeader}>
            <Typography variant="h5">Import Subjects</Typography>
            <IconButton className={classes.iconButton} onClick={handleCloseBadgesDataModal}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box className={classes.note}>
            <Typography className={classes.noteHead}>
              Effortlessly manage badge data for Attendees, Exhibitors, Speakers, and Sponsors.
            </Typography>
            <Box className={classes.noteSteps}>
              <Typography className={classes.noteValue}>
                1- Upload an Excel file ensuring it includes the following sheet names:{' '}
                <span className={classes.bold}>Attendees, Exhibitors, Speakers, and Sponsors</span>.
                Each sheet should contain the columns titled{' '}
                <span className={classes.bold}>FullName and Email</span>.
              </Typography>
              <Typography className={classes.noteValue}>
                2- After a successful import, you can easily select and print their badges.
              </Typography>
            </Box>
          </Box>

          <Box className={classes.uploadBox}>
            <Button
              color="primary"
              size="small"
              className={classes.upload}
              onClick={() => inputFileRef.current.click()}
              disabled={parsingFile}>
              {excelFile ? excelFile.name : 'Upload an excel file'}
              <input
                hidden
                id="file-input"
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileChange}
                ref={inputFileRef}
              />
            </Button>
            {parsingFile && (
              <Typography variant="body2" className={classes.infoMsg}>
                Your file is being processed. Please wait...
              </Typography>
            )}
            {!parsingFile && parsedFileData !== null && (
              <Typography variant="body2" className={classes.infoMsg}>
                File uploaded successfully. You may proceed.
              </Typography>
            )}
          </Box>

          <Box className={classes.actions}>
            <Button onClick={handleCloseBadgesDataModal} disabled={parsingFile}>
              Cancel
            </Button>
            <ProgressButton
              size="medium"
              label="Import"
              disabled={!excelFile || parsingFile || addingData}
              inProgress={addingData}
              onClick={handleImportSubjects}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog fullWidth maxWidth="lg" open={openAddSubjectModal || !!editedSubjectId}>
        <DialogContent className={classes.modalContent}>
          <Stack direction="row" className={classes.modalHeader}>
            <Typography variant="h5">{editedSubjectId ? 'Edit Subject' : 'Add Subject'}</Typography>
            <IconButton className={classes.iconButton} onClick={handleCloseAddSubjectModal}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>

          <BadgeSubjectForm onClose={handleCloseAddSubjectModal} subjectId={editedSubjectId} />
        </DialogContent>
      </Dialog>

      <ConfirmModal
        dialogTitle="Delete Subject"
        dialogText="Do you really want to delete this subject?"
        open={!!deletedSubjectId}
        closeModal={handleCloseDeleteModal}
        handleConfirm={handleDeleteBadgeSubject}
        inProgress={deletingSubject}
      />

      <ConfirmModal
        dialogTitle="Delete Selected Subject(s)"
        dialogText={`Do you really want to delete ${deletedSubjects?.length} subject?`}
        open={!!openBulkDeleteModal}
        closeModal={handleCloseBulkDeleteModal}
        handleConfirm={deleteSubjectsInBulk}
        inProgress={deletingSubjects}
      />

      <Dialog fullWidth maxWidth="sm" open={pdfConfigModal}>
        <DialogContent className={classes.modalContent}>
          <Stack direction="row" className={classes.modalHeader}>
            <Typography variant="h5">Badge Printing</Typography>
            <IconButton
              className={classes.iconButton}
              onClick={closePdfConfigModal}
              disabled={printingBadges}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Formik
            enableReinitialize
            initialValues={{
              pageSize: 'letter',
              orientation: localStorage.getItem('badgeOrientation') || 'portrait'
            }}
            validationSchema={pdfConfigurationSchema}
            onSubmit={handleFormSubmit}>
            <Form className={classes.pdfForm}>
              <CustomSelect
                inputLabel="Page Size"
                options={[
                  { id: 'letter', title: 'US Letter' },
                  { id: 'a4', title: 'A4' }
                ]}
                optionAttr="title"
                name="pageSize"
              />

              <CustomSelect
                inputLabel="Badge Orientation"
                options={[
                  { id: 'portrait', title: 'Portrait' },
                  { id: 'landscape', title: 'Landscape' }
                ]}
                optionAttr="title"
                name="orientation"
              />

              <Box className={classes.buttonBox}>
                <Button
                  className={classes.modalButton}
                  type="submit"
                  onClick={() => setSubmissionType('download')}
                  variant="contained"
                  color="primary"
                  disabled={printingBadges || isPrinting}
                  startIcon={<Download />}>
                  Download to PDF
                  {printingBadges && (
                    <CircularProgress size={18} className={classes.downloadCircle} />
                  )}
                </Button>
                <Button
                  className={classes.modalButton}
                  type="submit"
                  onClick={() => setSubmissionType('print')}
                  variant="contained"
                  color="primary"
                  disabled={printingBadges || isPrinting}
                  startIcon={<Print />}>
                  Direct Print
                  {isPrinting && <CircularProgress size={18} className={classes.downloadCircle} />}
                </Button>
              </Box>
            </Form>
          </Formik>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default BadgePrinting

const useStyles = makeStyles(() => ({
  modalButton: {
    textTransform: 'none',
    letterSpacing: '1px',
    fontSize: '1rem'
  },
  buttonBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  box: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    padding: '1.5rem 2rem',
    backgroundColor: 'white',
    borderRadius: '16px'
  },
  pdfForm: {
    marginTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    marginBottom: '1rem'
  },
  downloadCircle: {
    marginLeft: '0.75rem'
  },
  deleteBtn: {
    backgroundColor: '#d32f2f',
    color: 'white',
    '&:hover': {
      backgroundColor: '#b71c1c',
      color: 'white'
    }
  },
  modalHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  iconButton: {
    padding: '4px',
    '&.Mui-disabled': {
      opacity: '0.5'
    }
  },
  exportButton: {
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '15px',
    whiteSpace: 'nowrap'
  },
  configButton: {
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '15px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  upload: {
    textTransform: 'none',
    width: '100%',
    justifyContent: 'start',
    padding: '12px',
    fontSize: '13px',
    fontWeight: 600,
    border: '1px solid #D9E0E9',
    borderRadius: '24px',
    background: '#FFFFFF',
    marginRight: '20px'
  },
  modalContent: {
    paddingTop: '8px !important',
    paddingBottom: '8px'
  },
  uploadBox: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '1rem' },
  infoMsg: {
    marginLeft: '16px'
  },
  actions: {
    marginTop: '30px',
    marginBottom: '10px',
    display: 'flex',
    justifyContent: 'end',
    gap: '8px'
  },
  select: {
    width: '200px'
  },
  filterDropdown: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    justifyContent: 'end'
  },
  filterLabel: {
    width: '100%',
    fontSize: '1rem',
    textAlign: 'right',
    marginRight: '8px',
    fontWeight: 'bold'
  },
  note: {
    padding: '12px',
    borderRadius: '12px',
    backgroundColor: '#caedf5'
  },
  noteHead: {
    letterSpacing: '0.5px'
  },
  bold: {
    fontWeight: '600'
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingLeft: '8px'
  },
  noteValue: {
    marginTop: '0.2rem',
    fontSize: '14px',
    fontWeight: '500'
  }
}))
